import {
  type GraphTopologyParameters,
  type NodeData,
  type NodeSn,
  isTopologyRequestParameters,
} from '@ipfabric/utils';
import { performUndoableAction } from 'components/Graph/commands/history/performUndoableAction';
import type { DraftStateUpdater } from 'utils/react/StateProxy';
import type { CommandDispatcher } from 'utils/react/useCommandDispatcher';
import type { GraphState } from '../GraphState';
import { isNodeExpandable } from '../helpers/IsDeviceExpandable';
import type {
  GraphCommandContext,
  GraphCommandFactory,
} from '../model/GraphCommandContext';
import { loadGraph } from './loadGraph';

export interface ToggleNodeChildrenCommand {
  nodeData: NodeData;
}

type UpdateExpandDeviceGroups = () => void;

const makeUpdateExpandDeviceGroups =
  (
    updateState: DraftStateUpdater<GraphState>,
    dispatch: CommandDispatcher<GraphCommandContext>,
    parameters: GraphTopologyParameters,
    expandDeviceGroups: NodeSn[],
  ): UpdateExpandDeviceGroups =>
  () => {
    updateState((draft) => {
      draft.technologies.selectedTechnologies.expandDeviceGroups =
        expandDeviceGroups;
    });

    dispatch(loadGraph, {
      overlay: 'keep',
      parameters: {
        ...parameters,
        technologies: {
          ...parameters.technologies,
          expandDeviceGroups,
        },
      },
      preserveHistory: true,
    });
  };

export const toggleExpandDeviceGroup: GraphCommandFactory<
  ToggleNodeChildrenCommand
> = (cmdContext, dispatch) => (command) => {
  const parameters = cmdContext.getState().graphMetadata?.parameters;

  if (!isTopologyRequestParameters(parameters)) {
    return;
  }

  const expandDeviceGroups =
    cmdContext.getState().technologies.selectedTechnologies
      .expandDeviceGroups ?? [];

  if (isNodeExpandable(command.nodeData)) {
    const updatedExpandDeviceGroups: NodeSn[] = expandDeviceGroups?.includes(
      command.nodeData.sn,
    )
      ? expandDeviceGroups.filter((sn) => sn !== command.nodeData.sn)
      : [...expandDeviceGroups, command.nodeData.sn];

    dispatch(performUndoableAction, {
      undo: makeUpdateExpandDeviceGroups(
        cmdContext.updateState,
        dispatch,
        parameters,
        expandDeviceGroups,
      ),
      redo: makeUpdateExpandDeviceGroups(
        cmdContext.updateState,
        dispatch,
        parameters,
        updatedExpandDeviceGroups,
      ),
    });
  }
};
